/**
 * PanelTabs — sdílený chrome multi-tab WidgetPanel (Start plocha / Command Center).
 */
import { Pin, PinOff, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/Button';
import type { WidgetPanel as Panel } from '../widgets/types';
import type { PanelsState } from '../widgets/panels-store';

/** Default záložky — překlad podle id, i když je v prefs uložená čeština. */
const DEFAULT_TAB_KEYS: Record<string, string> = {
  ops: 'dashboard.tabOps',
  fleet: 'dashboard.tabFleet',
  health: 'dashboard.tabHealth',
  prehled: 'infra.tabOverview',
};

export function PanelTabs(props: {
  state: PanelsState;
  active: Panel;
  editing: boolean;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onTogglePin?: (p: Panel) => void;
}) {
  const { state, active, editing, onSelect, onAdd, onDelete, onTogglePin } = props;
  const { t } = useTranslation();

  const tabLabel = (p: Panel) => {
    const key = DEFAULT_TAB_KEYS[p.id];
    return key ? t(key, p.label) : p.label;
  };

  return (
    <div className='flex items-center gap-1 border-b border-gray-800/60 pb-2 flex-wrap'>
      {state.panels.map((p) => (
        <div key={p.id} className='flex items-center'>
          <button
            type='button'
            onClick={() => onSelect(p.id)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              p.id === active.id
                ? 'bg-plex/20 text-plex border border-plex/30'
                : 'text-gray-500 hover:text-white'
            }`}
          >
            {tabLabel(p)}
          </button>
          {onTogglePin && p.id === active.id && (
            <button
              type='button'
              onClick={() => onTogglePin(p)}
              className={`p-1 ${p.pinned ? 'text-plex' : 'text-gray-600 hover:text-gray-300'}`}
              title={p.pinned
                ? t('myPanel.unpin', 'Odepnout ze sidebaru')
                : t('myPanel.pin', 'Připnout na sidebar')}
            >
              {p.pinned ? <Pin size={13} /> : <PinOff size={13} />}
            </button>
          )}
          {editing && p.id === active.id && state.panels.length > 1 && (
            <button
              type='button'
              onClick={() => onDelete(p.id)}
              className='p-1 text-gray-600 hover:text-red-400'
              title={t('myPanel.delete', 'Smazat záložku')}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}
      <Button variant='ghost' size='sm' icon={<Plus size={14} />} onClick={onAdd} className='ml-1'>
        {t('myPanel.addTab', 'Záložka')}
      </Button>
    </div>
  );
}
